import { Pagination } from 'antd';
import { PaginationConfig } from 'antd/lib/pagination';
import './VtxPagination.scss';
import * as React from 'react';
import { FunctionComponent } from 'react';
import classNames from 'classnames';
import deepmerge from 'deepmerge';
import { IAutomatable, preventDataAutomationOverriding } from '../../helpers/IAutomatable';

export const defaultPaginationOptions: PaginationConfig = {
    showQuickJumper: true,
    showSizeChanger: true,
    showTitle: false,
    responsive: true,
    showLessItems: true,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} rows`,
    itemRender: (_current, type, originalElement) => {
        const notFocusable = -1;
        if (type === 'prev') {
            return <input type="button" value="prev" tabIndex={notFocusable} />;
        }
        if (type === 'next') {
            return <input type="button" value="next" tabIndex={notFocusable} />;
        }
        return originalElement;
    },
};

export interface VtxPaginationConfig
    extends Omit<PaginationConfig, 'showTotal' | 'itemRender'>,
        IAutomatable {
    /**
     * @deprecated for consistency sake, consumers can not override this
     * @default custom function
     */
    showTotal?: undefined;
    /**
     * @deprecated for consistency sake, consumers can not override this
     * @default custom function
     */
    itemRender?: never;
    /**
     * Determine whether you can jump to pages directly
     * @default true
     */
    showQuickJumper?: boolean;
    /**
     * Determine whether to show pageSize select
     * @default true
     */
    showSizeChanger?: boolean;
    /**
     * Show page item's title
     * @default false
     */
    showTitle?: boolean;
    /**
     * If size is not specified, Pagination would resize according to the width of the window
     * @default true
     */
    responsive?: boolean;
    /**
     * Use responsive instead if possible. This sets the size manually.
     * @default undefined
     */
    size?: PaginationConfig['size'];
    /**
     * Determines if it should show many page buttons or only a few
     * @default true
     */
    showLessItems?: boolean;
}

export const mergePaginationOptions = (input: VtxPaginationConfig | false | undefined): PaginationConfig => {
    const nameOfPaginationHideOnOnePage: keyof VtxPaginationConfig = 'hideOnSinglePage';
    const noOptionsSoTheDefaultWins = {};

    let configToStartWith: VtxPaginationConfig;
    if (input === undefined) {
        configToStartWith = noOptionsSoTheDefaultWins;
    } else if (input === false) {
        // eslint-disable-next-line no-console
        console.warn(
            `The UX designers would like all tables to paginate. You selected false for the pagination object. Perhaps what you're looking for is to set "${nameOfPaginationHideOnOnePage}" property on the "pagination" config object.`
        );
        configToStartWith = noOptionsSoTheDefaultWins;
    } else {
        configToStartWith = input;
    }

    return deepmerge(defaultPaginationOptions, configToStartWith);
};

export const VtxPagination: FunctionComponent<VtxPaginationConfig> = React.memo(
    (props: VtxPaginationConfig) => {
        const automationClass = 'vtx-pagination';
        const { className, ...otherProps } = mergePaginationOptions(preventDataAutomationOverriding(props));

        const classNamesCombined = classNames(automationClass, className);

        return (
            <div data-automation-class={automationClass} className={classNamesCombined}>
                <Pagination {...otherProps} />
            </div>
        );
    }
);
